"use client";

import { useMemo, useSyncExternalStore } from "react";

const LOCATION_CHANGE_EVENT = "explore-location-change";

declare global {
  interface Window {
    __exploreHistoryPatched?: boolean;
  }
}

function dispatchLocationChange() {
  window.dispatchEvent(new Event(LOCATION_CHANGE_EVENT));
}

function patchHistoryMethods() {
  if (window.__exploreHistoryPatched) {
    return;
  }

  const originalPushState = window.history.pushState.bind(window.history);
  const originalReplaceState = window.history.replaceState.bind(window.history);

  window.history.pushState = (...args) => {
    originalPushState(...args);
    dispatchLocationChange();
  };

  window.history.replaceState = (...args) => {
    originalReplaceState(...args);
    dispatchLocationChange();
  };

  window.__exploreHistoryPatched = true;
}

function subscribe(onStoreChange: () => void) {
  if (typeof window === "undefined") {
    return () => {
      // No-op during server rendering.
    };
  }

  patchHistoryMethods();

  window.addEventListener("popstate", onStoreChange);
  window.addEventListener(LOCATION_CHANGE_EVENT, onStoreChange);

  return () => {
    window.removeEventListener("popstate", onStoreChange);
    window.removeEventListener(LOCATION_CHANGE_EVENT, onStoreChange);
  };
}

function getSnapshot() {
  if (typeof window === "undefined") {
    return "";
  }

  return window.location.search;
}

export function useBrowserSearchParams({
  initialSearch = "",
}: {
  initialSearch?: string;
} = {}) {
  const search = useSyncExternalStore(
    subscribe,
    getSnapshot,
    () => initialSearch
  );

  return useMemo(() => new URLSearchParams(search), [search]);
}
